import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CommandPalette } from "@/components/ui/command-palette";
import { GlobalKeyboardShortcutsProvider } from "@/components/GlobalKeyboardShortcutsProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SentryErrorBoundary } from "@/components/SentryErrorBoundary";
import { LazyLoadErrorBoundary } from "@/components/LazyLoadErrorBoundary";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { LoadingPage } from "@/components/ui/loading-skeleton";
import { ThemeProvider } from "next-themes";
import { DashboardDataProvider } from "@/contexts/DashboardDataContext";
import { InteractiveTour } from "@/components/onboarding/InteractiveTour";
import { TourProvider } from "@/contexts/TourContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import { ScrollToTop } from "@/components/ScrollToTop";
import { SkipToMain } from "@/components/a11y/SkipToMain";

// Eager load landing and auth pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import FeedbackForm from "./pages/FeedbackForm";
import EncodedAuthRedirect from "./components/auth/EncodedAuthRedirect";
import { CookieConsent } from "@/components/CookieConsent";
import { UpdateAvailablePrompt } from "@/components/UpdateAvailablePrompt";
import { PostHogConsentHandler } from "@/components/PostHogProvider";
import { initSentry } from "@/lib/sentry";

// Lazy load with retry for dashboard and heavy pages
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const TrackFeedback = lazyWithRetry(() => import("./pages/TrackFeedback"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const CookiePolicy = lazyWithRetry(() => import("./pages/CookiePolicy"));
const RefundPolicy = lazyWithRetry(() => import("./pages/RefundPolicy"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const Help = lazyWithRetry(() => import("./pages/Help"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const About = lazyWithRetry(() => import("./pages/About"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const AIHRAdvisor = lazyWithRetry(() => import("./pages/AIHRAdvisor"));
const AIInsightsHub = lazyWithRetry(() => import("./pages/AIInsightsHub"));
const Feedback = lazyWithRetry(() => import("./pages/FeedbackView"));
const NewFeedbackPage = lazyWithRetry(() => import("./pages/NewFeedbackPage"));
const EditFeedbackPage = lazyWithRetry(() => import("./pages/EditFeedbackPage"));
const ViewFeedback = lazyWithRetry(() => import("./pages/ViewFeedback"));
const FeedbackPages = lazyWithRetry(() => import("./pages/FeedbackPages"));
const GlobalAnalytics = lazyWithRetry(() => import("./pages/GlobalAnalytics"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Reports = lazyWithRetry(() => import("./pages/Reports"));
const Analytics = lazyWithRetry(() => import("./pages/Analytics"));
const Campaigns = lazyWithRetry(() => import("./pages/Campaigns"));
const CampaignAnalytics = lazyWithRetry(() => import("./pages/CampaignAnalytics"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const AcceptInvitation = lazyWithRetry(() => import("./pages/AcceptInvitation"));
const TrialExpired = lazyWithRetry(() => import("./pages/TrialExpired"));
const Referrals = lazyWithRetry(() => import("./pages/Referrals"));

// Lazy load admin pages with retry
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazyWithRetry(() => import("./pages/admin/Users"));
const AdminRevenue = lazyWithRetry(() => import("./pages/admin/Revenue"));
const AdminSecurity = lazyWithRetry(() => import("./pages/admin/Security"));
const AdminDataRetention = lazyWithRetry(() => import("./pages/admin/DataRetention"));
const AdminSupport = lazyWithRetry(() => import("./pages/admin/Support"));
const AdminCustomerHealth = lazyWithRetry(() => import("./pages/admin/CustomerHealth"));
const AdminMonitoring = lazyWithRetry(() => import("./pages/admin/Monitoring"));
const SystemHealth = lazyWithRetry(() => import("./pages/admin/SystemHealth"));
const APIDocs = lazyWithRetry(() => import("./pages/APIDocs"));
const ManagerDashboard = lazyWithRetry(() => import("./pages/ManagerDashboard"));
const NewCompany = lazyWithRetry(() => import("./pages/NewCompany"));
const AdminFeedback = lazyWithRetry(() => import("./pages/admin/AdminFeedback"));
const AdminAnalytics = lazyWithRetry(() => import("./pages/admin/AdminAnalytics"));
const AdminReferrals = lazyWithRetry(() => import("./pages/admin/AdminReferrals"));
const AdminApiKeys = lazyWithRetry(() => import("./pages/admin/AdminApiKeys"));
const AdminSettings = lazyWithRetry(() => import("./pages/admin/AdminSettings"));
const AdminNotifications = lazyWithRetry(() => import("./pages/admin/AdminNotifications"));
const AdminBusinessMetrics = lazyWithRetry(() => import("./pages/admin/BusinessMetrics"));
const AdminFeatureFlags = lazyWithRetry(() => import("./pages/admin/FeatureFlags"));
const AdminEmailLogs = lazyWithRetry(() => import("./pages/admin/EmailLogs"));
const AdminSystemStatus = lazyWithRetry(() => import("./pages/admin/SystemStatus"));
const AdminHelp = lazyWithRetry(() => import("./pages/admin/AdminHelp"));

// Lazy load solutions pages with retry
const HRTeams = lazyWithRetry(() => import("./pages/solutions/HRTeams"));
const RemoteTeams = lazyWithRetry(() => import("./pages/solutions/RemoteTeams"));
const EmployeeRetention = lazyWithRetry(() => import("./pages/solutions/EmployeeRetention"));
const Startups = lazyWithRetry(() => import("./pages/solutions/Startups"));
const Enterprise = lazyWithRetry(() => import("./pages/solutions/Enterprise"));
const CultureBuilding = lazyWithRetry(() => import("./pages/solutions/CultureBuilding"));

// Lazy load comparison pages with retry
const SurveyMonkeyAlternative = lazyWithRetry(() => import("./pages/compare/SurveyMonkeyAlternative"));
const TraditionalSurveys = lazyWithRetry(() => import("./pages/compare/TraditionalSurveys"));
const CultureAmpAlternative = lazyWithRetry(() => import("./pages/compare/CultureAmpAlternative"));
const AnonymousEmail = lazyWithRetry(() => import("./pages/compare/AnonymousEmail"));
const WhyAnonymousFeedback = lazyWithRetry(() => import("./pages/compare/WhyAnonymousFeedback"));
const HowAnonymityWorks = lazyWithRetry(() => import("./pages/HowAnonymityWorks"));

// Product feature pages
const ProductAIInsights = lazyWithRetry(() => import("./pages/product/AIInsights"));
const ProductHRActionPlans = lazyWithRetry(() => import("./pages/product/HRActionPlans"));
const ProductPulseCampaigns = lazyWithRetry(() => import("./pages/product/PulseCampaigns"));

// Programmatic SEO pages
const IndustryPage = lazyWithRetry(() => import("./pages/solutions/IndustryPage"));
const UseCasePage = lazyWithRetry(() => import("./pages/use-cases/UseCasePage"));
const CompetitorPage = lazyWithRetry(() => import("./pages/compare/CompetitorPage"));
const XFollowersOffer = lazyWithRetry(() => import("./pages/XFollowersOffer"));
const MarketingBrief = lazyWithRetry(() => import("./pages/MarketingBrief"));
const SharedConversation = lazyWithRetry(() => import("./pages/SharedConversation"));
const Investors = lazyWithRetry(() => import("./pages/Investors"));

// Ad landing pages
const GoogleAdsLanding = lazyWithRetry(() => import("./pages/ads/GoogleAdsLanding"));
const FacebookAdsLanding = lazyWithRetry(() => import("./pages/ads/FacebookAdsLanding"));
const InstagramAdsLanding = lazyWithRetry(() => import("./pages/ads/InstagramAdsLanding"));
const LinkedInAdsLanding = lazyWithRetry(() => import("./pages/ads/LinkedInAdsLanding"));

// Offer pages
const NewYearOffer = lazyWithRetry(() => import("./pages/offers/NewYearOffer"));

// Resource library (programmatic SEO)
const ResourcesHub = lazyWithRetry(() => import("./pages/resources/ResourcesHub"));
const ResourceArticle = lazyWithRetry(() => import("./pages/resources/ResourceArticle"));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // Data stays fresh for 1 minute
      gcTime: 300000, // Keep unused data in cache for 5 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 2, // Increased for network resilience
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    },
  },
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Reserved paths that should not be treated as company slugs
// IMPORTANT: Keep in sync with top-level route paths below
const RESERVED_PATHS = [
  // Core app routes
  'dashboard', 'admin', 'auth', 'f', 'pricing', 'help', 'contact', 'about',
  // Legal pages
  'privacy-policy', 'terms-of-service', 'cookie-policy', 'refund-policy',
  // Feature routes  
  'track', 'reset-password', 'unsubscribe', 'shared', 'solutions', 'compare',
  // Marketing/SEO routes
  'features', 'how-it-works', 'faq', 'how-anonymity-works', 'x-special', 'use-cases', 'industry', 'product', 'resources',
  // Onboarding/conversion routes
  'accept-invitation', 'new-company', 'ads', 'trial-expired', 'investors', 'offer',
  // API routes
  'api', 'api-docs', 'system-health', 'not-found'
] as const;

// Wrapper to prevent reserved paths from matching the company route
const CompanyFeedbackRoute = () => {
  const { company } = useParams();
  
  if (company && (RESERVED_PATHS as readonly string[]).includes(company.toLowerCase())) {
    return <Navigate to="/not-found" replace />;
  }
  
  return <FeedbackForm />;
};

const App = () => {
  return (
    <ErrorBoundary>
      <SentryErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider
            attribute="class"
            defaultTheme="light"
            enableSystem={false}
            disableTransitionOnChange
            storageKey="feedbackflow-theme"
          >
            <TooltipProvider>
              <CookieConsent />
              <UpdateAvailablePrompt />
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <SkipToMain />
                <ScrollToTop />
                <PostHogConsentHandler>
                  <AuthProvider>
                    <TourProvider>
                      <DashboardDataProvider>
                        <CommandPalette />
                        <GlobalKeyboardShortcutsProvider />
                        <InteractiveTour />
                        <SentryRoutes>
                  <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/trial-expired" element={
                <ProtectedRoute>
                  <Suspense fallback={<LoadingPage />}>
                    <TrialExpired />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/f/:slug" element={<FeedbackForm />} />
              <Route path="/track/:code?" element={
                <Suspense fallback={<LoadingPage />}>
                  <TrackFeedback />
                </Suspense>
              } />
              <Route path="/pricing" element={
                <Suspense fallback={<LoadingPage />}>
                  <Pricing />
                </Suspense>
              } />
              
              <Route path="/privacy-policy" element={
                <Suspense fallback={<LoadingPage />}>
                  <PrivacyPolicy />
                </Suspense>
              } />
              <Route path="/terms-of-service" element={
                <Suspense fallback={<LoadingPage />}>
                  <TermsOfService />
                </Suspense>
              } />
              <Route path="/help" element={
                <Suspense fallback={<LoadingPage />}>
                  <Help />
                </Suspense>
              } />
              <Route path="/contact" element={
                <Suspense fallback={<LoadingPage />}>
                  <Contact />
                </Suspense>
              } />
              <Route path="/cookie-policy" element={
                <Suspense fallback={<LoadingPage />}>
                  <CookiePolicy />
                </Suspense>
              } />
              <Route path="/refund-policy" element={
                <Suspense fallback={<LoadingPage />}>
                  <RefundPolicy />
                </Suspense>
              } />
              <Route path="/unsubscribe" element={
                <Suspense fallback={<LoadingPage />}>
                  <Unsubscribe />
                </Suspense>
              } />
              <Route path="/accept-invitation" element={
                <Suspense fallback={<LoadingPage />}>
                  <AcceptInvitation />
                </Suspense>
              } />
              <Route path="/shared/conversation/:token" element={
                <Suspense fallback={<LoadingPage />}>
                  <SharedConversation />
                </Suspense>
              } />

              {/* About Page */}
              <Route path="/about" element={
                <Suspense fallback={<LoadingPage />}>
                  <About />
                </Suspense>
              } />

              {/* Investors Page */}
              <Route path="/investors" element={
                <Suspense fallback={<LoadingPage />}>
                  <Investors />
                </Suspense>
              } />

              {/* Solutions Pages */}
              <Route path="/solutions/hr-teams" element={
                <Suspense fallback={<LoadingPage />}>
                  <HRTeams />
                </Suspense>
              } />
              <Route path="/solutions/remote-teams" element={
                <Suspense fallback={<LoadingPage />}>
                  <RemoteTeams />
                </Suspense>
              } />
              <Route path="/solutions/employee-retention" element={
                <Suspense fallback={<LoadingPage />}>
                  <EmployeeRetention />
                </Suspense>
              } />
              <Route path="/solutions/startups" element={
                <Suspense fallback={<LoadingPage />}>
                  <Startups />
                </Suspense>
              } />
              <Route path="/solutions/enterprise" element={
                <Suspense fallback={<LoadingPage />}>
                  <Enterprise />
                </Suspense>
              } />
              <Route path="/solutions/culture-building" element={
                <Suspense fallback={<LoadingPage />}>
                  <CultureBuilding />
                </Suspense>
              } />

              {/* Comparison Pages */}
              <Route path="/compare/surveymonkey-alternative" element={
                <Suspense fallback={<LoadingPage />}>
                  <SurveyMonkeyAlternative />
                </Suspense>
              } />
              <Route path="/compare/traditional-surveys" element={
                <Suspense fallback={<LoadingPage />}>
                  <TraditionalSurveys />
                </Suspense>
              } />
              <Route path="/compare/culture-amp-alternative" element={
                <Suspense fallback={<LoadingPage />}>
                  <CultureAmpAlternative />
                </Suspense>
              } />
              <Route path="/compare/anonymous-email" element={
                <Suspense fallback={<LoadingPage />}>
                  <AnonymousEmail />
                </Suspense>
              } />
              <Route path="/compare/why-anonymous-feedback" element={
                <Suspense fallback={<LoadingPage />}>
                  <WhyAnonymousFeedback />
                </Suspense>
              } />
              
              {/* How Anonymity Works */}
              <Route path="/how-anonymity-works" element={
                <Suspense fallback={<LoadingPage />}>
                  <HowAnonymityWorks />
                </Suspense>
              } />

              {/* Product feature pages */}
              <Route path="/product/ai-insights" element={
                <Suspense fallback={<LoadingPage />}>
                  <ProductAIInsights />
                </Suspense>
              } />
              <Route path="/product/hr-action-plans" element={
                <Suspense fallback={<LoadingPage />}>
                  <ProductHRActionPlans />
                </Suspense>
              } />
              <Route path="/product/pulse-campaigns" element={
                <Suspense fallback={<LoadingPage />}>
                  <ProductPulseCampaigns />
                </Suspense>
              } />

              {/* Programmatic SEO: Industry Pages */}
              <Route path="/solutions/industry/:slug" element={
                <Suspense fallback={<LoadingPage />}>
                  <IndustryPage />
                </Suspense>
              } />

              {/* Programmatic SEO: Use Case Pages */}
              <Route path="/use-cases/:slug" element={
                <Suspense fallback={<LoadingPage />}>
                  <UseCasePage />
                </Suspense>
              } />

              {/* Programmatic SEO: Competitor Comparison Pages */}
              <Route path="/compare/:slug" element={
                <Suspense fallback={<LoadingPage />}>
                  <CompetitorPage />
                </Suspense>
              } />

              {/* Resource library — programmatic SEO content hub */}
              <Route path="/resources" element={
                <Suspense fallback={<LoadingPage />}>
                  <ResourcesHub />
                </Suspense>
              } />
              <Route path="/resources/:slug" element={
                <Suspense fallback={<LoadingPage />}>
                  <ResourceArticle />
                </Suspense>
              } />
              
              {/* Homepage section redirects */}
              <Route path="/features" element={<Navigate to="/#features" replace />} />
              <Route path="/how-it-works" element={<Navigate to="/#how-it-works" replace />} />
              <Route path="/faq" element={<Navigate to="/#faq" replace />} />
              
              {/* X Followers Special Offer */}
              <Route path="/x-special" element={
                <Suspense fallback={<LoadingPage />}>
                  <XFollowersOffer />
                </Suspense>
              } />
              
              {/* Marketing Brief */}
              <Route path="/marketing-brief" element={
                <Suspense fallback={<LoadingPage />}>
                  <MarketingBrief />
                </Suspense>
              } />
              
              {/* Ad Landing Pages */}
              <Route path="/ads/employee-feedback" element={
                <Suspense fallback={<LoadingPage />}>
                  <GoogleAdsLanding />
                </Suspense>
              } />
              <Route path="/ads/save-your-team" element={
                <Suspense fallback={<LoadingPage />}>
                  <FacebookAdsLanding />
                </Suspense>
              } />
              <Route path="/ads/culture-trust" element={
                <Suspense fallback={<LoadingPage />}>
                  <InstagramAdsLanding />
                </Suspense>
              } />
              <Route path="/ads/hr-leaders" element={
                <Suspense fallback={<LoadingPage />}>
                  <LinkedInAdsLanding />
                </Suspense>
              } />
              
              {/* Offer Landing Pages */}
              <Route path="/offer/newyear" element={
                <Suspense fallback={<LoadingPage />}>
                  <NewYearOffer />
                </Suspense>
              } />
              
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <Dashboard />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/ai-hub" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <AIInsightsHub />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              {/* Legacy redirect — keep external links / bookmarks working */}
              <Route path="/dashboard/ai-insights" element={<ProtectedRoute><Navigate to="/dashboard/ai-hub" replace /></ProtectedRoute>} />
              {/* Keep HR Advisor as separate deep-dive page */}
              <Route path="/dashboard/hr-advisor" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <AIHRAdvisor />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/manager" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <ManagerDashboard />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/skills-gap" element={
                <Navigate to="/dashboard/ai-hub?tab=skills" replace />
              } />
              <Route path="/dashboard/feedback" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <Feedback />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/pages" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <FeedbackPages />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/pages/:pageId" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <ViewFeedback />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/pages/:pageId/edit" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <EditFeedbackPage />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/new" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <NewFeedbackPage />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/analytics" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <GlobalAnalytics />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/analytics/:pageId" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <Analytics />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/settings" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <Settings />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              {/* Redirects from old account and billing routes */}
              <Route path="/dashboard/account" element={<ProtectedRoute><Navigate to="/dashboard/settings?tab=account" replace /></ProtectedRoute>} />
              <Route path="/dashboard/billing" element={<ProtectedRoute><Navigate to="/dashboard/settings?tab=billing" replace /></ProtectedRoute>} />
              <Route path="/dashboard/billing-history" element={<ProtectedRoute><Navigate to="/dashboard/settings?tab=history" replace /></ProtectedRoute>} />
              <Route path="/dashboard/team" element={<ProtectedRoute><Navigate to="/dashboard/settings?tab=team" replace /></ProtectedRoute>} />
              <Route path="/dashboard/reports" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <Reports />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/referrals" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <Referrals />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/api-docs" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <APIDocs />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/new-company" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <NewCompany />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/campaigns" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <Campaigns />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/campaigns/analytics" element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LazyLoadErrorBoundary>
                      <Suspense fallback={<LoadingPage />}>
                        <CampaignAnalytics />
                      </Suspense>
                    </LazyLoadErrorBoundary>
                  </DashboardLayout>
                </ProtectedRoute>
              } />
              
              {/* Admin Routes */}
            <Route path="/admins/dashboard" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminDashboard />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/users" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminUsers />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/revenue" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminRevenue />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/security" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminSecurity />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/data-retention" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminDataRetention />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/monitoring" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminMonitoring />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/support" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminSupport />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/customer-health" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminCustomerHealth />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/health" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <SystemHealth />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/feedback" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminFeedback />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/analytics" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminAnalytics />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/referrals" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminReferrals />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/api-keys" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminApiKeys />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/notifications" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminNotifications />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/payments" element={<Navigate to="/admins/revenue" replace />} />
            <Route path="/admins/settings" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminSettings />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/business-metrics" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminBusinessMetrics />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/feature-flags" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminFeatureFlags />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/email-logs" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminEmailLogs />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/system-status" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminSystemStatus />
                </Suspense>
              </AdminLayout>
            } />
            <Route path="/admins/help" element={
              <AdminLayout>
                <Suspense fallback={<LoadingPage />}>
                  <AdminHelp />
                </Suspense>
              </AdminLayout>
            } />
              
            {/* Explicit 404 route */}
            <Route path="/404" element={
              <Suspense fallback={<LoadingPage />}>
                <NotFound />
              </Suspense>
            } />

            {/* Handle URL-encoded auth paths like /auth%3Fmode=signup */}
            <Route path="/:encodedPath" element={<EncodedAuthRedirect />} />
            
            {/* Company feedback form - MUST be after all specific routes */}
            <Route path="/:company/:slug" element={<CompanyFeedbackRoute />} />
            
            <Route path="*" element={
                <Suspense fallback={<LoadingPage />}>
                  <NotFound />
                </Suspense>
              } />
                  </SentryRoutes>
                </DashboardDataProvider>
              </TourProvider>
            </AuthProvider>
          </PostHogConsentHandler>
        </BrowserRouter>
            </TooltipProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </SentryErrorBoundary>
    </ErrorBoundary>
  );
};

export default App;
